<template>
    <div class="vehicle">
        <transition name="validation-error">
            <div class="vehicle__error validation-error"
                 v-if="showError">
                {{ $t('validations.requiredAllFields') }}
            </div>
        </transition>

        <form class="vehicle__form">

            <div class="vehicle__groups">
                <v-select class="vehicle__select jlr-select"
                          :class="{
                                '_error': submitClicked && !selectedModel,
                                '_loading': modelsLoading
                          }"
                          v-model="selectedModel"
                          :options="models"
                          :getOptionLabel="getOptionLabel"
                          :placeholder="$t('common.model')"
                          label="name">

                    <span class="no-items" slot="no-options">
                        {{ $t('maintenance.noMatchingModels') }}
                    </span>
                </v-select>

                <v-select class="vehicle__select jlr-select"
                          :class="{
                                '_error': submitClicked && !selectedAir,
                                '_loading': airTypesLoading
                          }"
                          :disabled="selectedModel === ''"
                          v-model="selectedAir"
                          :options="airTypes"
                          :getOptionLabel="getOptionLabel"
                          :placeholder="$t('maintenance.airType') "
                          label="name">

                    <span class="no-items" slot="no-options">
                        {{ $t('maintenance.noMatchingAirTypes') }}
                    </span>
                </v-select>

                <v-select class="vehicle__select jlr-select"
                          :class="{
                                '_error': submitClicked && !selectedFuel,
                                '_loading': fuelTypesLoading
                          }"
                          :disabled="selectedAir === ''"
                          v-model="selectedFuel"
                          :options="fuelTypes"
                          :getOptionLabel="getOptionLabel"
                          :placeholder="$t('maintenance.fuel') "
                          label="name">
                    <span class="no-items" slot="no-options">
                        {{ $t('maintenance.noMatchingFuelTypes') }}
                    </span>
                </v-select>

                <v-select class="vehicle__select jlr-select"
                          :class="{
                                '_error': submitClicked && !selectedEngineSize,
                                '_loading': enginesLoading
                          }"
                          :disabled="selectedFuel === ''"
                          v-model="selectedEngineSize"
                          :options="engines"
                          :getOptionLabel="getOptionLabel"
                          :placeholder="$t('maintenance.engineSize') "
                          label="name">
                    <span class="no-items" slot="no-options">
                        {{ $t('maintenance.noMatchingEngineSizes') }}
                    </span>
                </v-select>

                <v-select class="vehicle__select jlr-select"
                          :class="{
                                '_error': submitClicked && !selectedModelYear,
                                '_loading': modelYearsLoading
                          }"
                          :disabled="selectedEngineSize === ''"
                          v-model="selectedModelYear"
                          :options="modelYears"
                          :getOptionLabel="getOptionLabel"
                          :placeholder="$t('maintenance.modelYear') "
                          label="name">
                    <span class="no-items" slot="no-options">
                        {{ $t('maintenance.noMatchingModelYears') }}
                    </span>
                </v-select>

                <div class="vehicle__group">
                    <input class="vehicle__input"
                           :class="{ '_error': $v.mileage.$error }"
                           :placeholder="$t('maintenance.mileage')"
                           v-model.trim="mileage"/>

                    <transition name="validation-error">
                        <div class="vehicle__errors"
                             v-if="$v.mileage.$error && selectedModelYear !== ''">
                            <div class="validation-error"
                                 v-if="!$v.mileage.numeric">
                                {{ $t('validations.numeric', { name: $t('maintenance.mileage') }) }}
                            </div>
                            <div class="validation-error"
                                 v-if="!$v.mileage.maxLength && $v.mileage.numeric">
                                {{  $t('validations.maxLength',
                                { name: $t('maintenance.mileage'), value: $v.mileage.$params.maxLength.max })
                                }}
                            </div>
                            <div class="validation-error"
                                 v-if="!$v.mileage.minValue && $v.mileage.numeric">
                                {{  $t('validations.zeroValue', { name: $t('maintenance.mileage')}) }}
                            </div>
                        </div>
                    </transition>

                </div>

                <div class="vehicle__group _big">
                    <user-location class="user-location"
                                   :class="{'_error': submitClicked && !location}"
                                   :limitedСitiesList="cities"
                                   @changeUserLocation="setLocation($event)"
                                   @blur="$v.location.$touch()"/>
                </div>

            </div>

            <div class="vehicle__button">
                <submit-button @click="submit"
                               :sending="sending"/>
            </div>

        </form>
    </div>
</template>

<script>
    import { required, numeric, maxLength, minValue } from 'vuelidate/lib/validators'
    import axios from 'axios'
    import { maintenanceCatalog as catalog } from '../urlConstructor/maintenanceCatalog'

    import vSelect from 'vue-select'
    import UserLocation from './UserLocation.vue'

    export default {
        name: 'VehicleInfoForm',
        props: {
            sending: {
                type: Boolean,
                required: true
            }
        },
        data() {
            return {
                selectedModel: '',
                selectedAir: '',
                selectedFuel: '',
                selectedEngineSize: '',
                selectedModelYear: '',
                mileage: '',
                location: '',

                models:[],
                airTypes: [],
                fuelTypes: [],
                engines: [],
                modelYears: [],

                modelsLoading: false,
                airTypesLoading: false,
                fuelTypesLoading: false,
                enginesLoading: false,
                modelYearsLoading: false,

                submitClicked: false
            }
        },
        computed: {
            showError() {
                return this.submitClicked && (this.selectedModelYear === '' || !this.$v.mileage.required || !this.$v.location.required)
            },
            cities() {
                return this.$store.state.maintenance.cities
            },
            brandId() {
                return this.$store.state.brandId;
            }
        },
        validations: {
            mileage: {
                required,
                numeric,
                maxLength: maxLength(6),
                minValue: minValue(1)
            },
            selectedModelYear: {
                required
            },
            location: {
                required
            }
        },
        methods: {
            setLocation($event) {
                this.location = $event;
            },

            submit() {
                this.submitClicked = true;
                this.$v.mileage.$touch();
                this.$v.location.$touch();
                if (!this.$v.$invalid) {
                    this.$emit('vehicleInfoUpdated', {
                        model: this.selectedModel,
                        air: this.selectedAir,
                        fuel: this.selectedFuel,
                        engineSize: this.selectedEngineSize,
                        year: this.selectedModelYear,
                        mileage: this.mileage
                    })
                }
            },

            getOptionLabel(option) {
                return option.name;
            }
        },
        watch: {
            selectedModel(val, oldVal) {
                const urlParams = {
                    brandId: this.brandId,
                    modelId: val.id
                };

                this.airTypesLoading = true;
                axios.get(catalog.getAirTypes(urlParams))
                    .then(res => {
                        this.airTypes = res.data.items;
                        this.airTypesLoading = false;
                    }).catch(err => {
                        console.log('Could not retrieve air types. Errors: \n', err);
                        this.airTypesLoading = false;
                    });

                if( oldVal !== '' ) {
                    this.selectedAir = '';
                    this.selectedFuel = '';
                    this.selectedEngineSize = '';
                    this.selectedModelYear = '';
                }
            },

            selectedAir(val, oldVal) {
                if( val !== '') {
                    const urlParams = {
                        brandId: this.brandId,
                        modelId: this.selectedModel.id,
                        airTypeId: val.id
                    };

                    this.fuelTypesLoading = true;
                    axios.get(catalog.getFuelTypes(urlParams))
                        .then(res => {
                            this.fuelTypes = res.data.items;
                            this.fuelTypesLoading = false;
                        }).catch(err => {
                            console.log('Could not retrieve fuel types. Errors: \n', err);
                            this.fuelTypesLoading = false;
                        });
                }


                if( oldVal !== '' ) {
                    this.selectedFuel = '';
                    this.selectedEngineSize = '';
                    this.selectedModelYear = '';
                }
            },

            selectedFuel(val, oldVal) {
                if( val !== '') {
                    const urlParams = {
                        brandId: this.brandId,
                        modelId: this.selectedModel.id,
                        airTypeId: this.selectedAir.id,
                        fuelTypeId: val.id
                    };


                    this.enginesLoading = true;
                    axios.get(catalog.getEngines(urlParams))
                        .then(res => {
                            this.engines = res.data.items;
                            this.enginesLoading = false;
                        }).catch(err => {
                            console.log('Could not retrieve engines. Errors: \n', err);
                            this.enginesLoading = false;
                        });
                }

                if( oldVal !== '' ) {
                    this.selectedEngineSize = '';
                    this.selectedModelYear = '';
                }
            },

            selectedEngineSize(val, oldVal) {
                if( val !== '') {
                    const urlParams = {
                        brandId: this.brandId,
                        modelId: this.selectedModel.id,
                        airTypeId: this.selectedAir.id,
                        fuelTypeId: this.selectedFuel.id,
                        engine: val.id
                    };


                    this.modelYearsLoading = true;
                    axios.get(catalog.getModelYears(urlParams))
                        .then(res => {
                            this.modelYears = res.data.items;
                            this.modelYearsLoading = false;
                        }).catch(err => {
                            console.log('Could not retrieve model years. Errors: \n', err);
                            this.modelYearsLoading = false;
                        });
                }

                if( oldVal !== '' ) {
                    this.selectedModelYear = '';
                }
            }
        },
        beforeMount() {
            const urlParams = {
                brandId: this.brandId
            };

            this.modelsLoading = true;
            axios.get(catalog.getModels(urlParams))
                .then(res => {
                    this.models = res.data.items;
                    this.modelsLoading = false;
                }).catch(err => {
                    console.log('Could not retrieve models. Errors: \n', err);
                    this.modelsLoading = false;
                });

            const infoInStore = this.$store.state.maintenanceSelected.vehicleInfo;
            if (infoInStore.mileage && this.$store.state.maintenanceSelected.vehicleInfoEntryType === 'info') {
                this.mileage = infoInStore.mileage;
                this.selectedModel = infoInStore.model;
                this.selectedAir = infoInStore.air;
                this.selectedFuel = infoInStore.fuel;
                this.selectedEngineSize = infoInStore.engine;
                this.selectedModelYear = infoInStore.year;
            }
        },
        components: {
            vSelect,
            UserLocation
        }
    }
</script>

<style lang="less">
    @import (less) '../styles/_media.less';
    @import (less) '../styles/colors.less';

    .vehicle {
        &__group {
            @media @tablet {
                width: 153px;
            }
            @media @desktop {
                width: 163px;
            }

            &._big {
                @media @tablet {
                    width: 320px;
                }
                @media @desktop {
                    width: 340px;
                }
            }
        }

        &__select {
            width: 100%;
            margin-bottom: 10px;

            @media @tablet {
                width: 153px;
                display: inline-block;
                margin-right: 10px;
            }
            @media @desktop {
                width: 163px;
            }

            &._loading {
                &::after {
                    content: '';
                    position: absolute;
                    top: 50%;
                    right: 0;
                    transform: translateY(-50%);
                    background: url(../icons/loaders_icon_small.gif) no-repeat 75%,
                                linear-gradient(to left, white 50%, @transparent) !important;
                    width: 45px !important;
                    height: calc(~'100% - 2px');
                }
            }

            &.disabled {
                &::after {
                    content: '';
                    position: absolute;
                    top: 50%;
                    right: 0;
                    transform: translateY(-50%);
                    background: linear-gradient(to left, white 50%, @transparent) !important;
                    width: 45px !important;
                    height: calc(~'100% - 2px');
                }

                .open-indicator {
                    &::after {
                        cursor: not-allowed !important;
                        opacity: .3;
                        z-index: 10;
                    }
                }
            }
        }

        &__error {
            padding-bottom: 10px;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/VehicleInfoForm.vue